import React from 'react';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null,
      retryCount: 0,
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI.
    return {
      hasError: true,
      error,
      errorId: `error_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    };
  }

  componentDidCatch(error, errorInfo) {
    // Enhanced error logging with more context
    const errorContext = {
      error,
      errorInfo,
      errorId: this.state.errorId,
      timestamp: new Date().toISOString(),
      url: window.location.href,
      userAgent: navigator.userAgent,
      retryCount: this.state.retryCount,
      componentStack: errorInfo?.componentStack,
      errorBoundary: this.constructor.name,
    };

    this.setState({ error, errorInfo });

    // Enhanced console logging
    console.group('🚨 ErrorBoundary Caught Error');
    console.error('Error:', error);
    console.error('Error Info:', errorInfo);
    console.error('Error ID:', this.state.errorId);
    console.error('Full Context:', errorContext);
    console.groupEnd();

    // Send to error reporting service (if configured)
    this.reportError(errorContext);
  }

  reportError = (errorContext) => {
    // You can integrate with services like Sentry, LogRocket, etc.
    try {
      // Example: Send to your backend
      // fetch('/api/errors', {
      //   method: 'POST',
      //   headers: { 'Content-Type': 'application/json' },
      //   body: JSON.stringify(errorContext)
      // });

      // For now, just log to console with structured data
      console.log('📊 Error Report Generated:', errorContext);
    } catch (reportingError) {
      console.error('Failed to report error:', reportingError);
    }
  };

  handleRetry = () => {
    this.setState((prevState) => ({
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: prevState.retryCount + 1,
    }));
  };

  getErrorType = (error) => {
    if (error?.name === 'ChunkLoadError') return 'Code Splitting Error';
    if (error?.name === 'TypeError') return 'Type Error';
    if (error?.name === 'ReferenceError') return 'Reference Error';
    if (error?.name === 'SyntaxError') return 'Syntax Error';
    if (error?.message?.includes('Network')) return 'Network Error';
    if (error?.message?.includes('timeout')) return 'Timeout Error';
    if (error?.message?.includes('permission')) return 'Permission Error';
    return 'Unknown Error Type';
  };

  getErrorSeverity = (error) => {
    if (error?.name === 'ChunkLoadError') return 'medium';
    if (error?.message?.includes('Network')) return 'low';
    if (error?.name === 'TypeError' && error?.message?.includes('Cannot read')) return 'high';
    return 'medium';
  };

  getSuggestedSolutions = (errorType) => {
    const solutions = {
      'Code Splitting Error': [
        'Try refreshing the page to reload the failed chunk',
        'Clear your browser cache and cookies',
        'Check your internet connection',
        'If the problem persists, contact support',
      ],
      'Type Error': [
        'This is likely a programming error in the application',
        'Try refreshing the page',
        'If the error persists, report it to the development team',
        'Check if you have the latest version of the application',
      ],
      'Network Error': [
        'Check your internet connection',
        'Try refreshing the page',
        'Check if the server is running',
        'Try again in a few minutes',
      ],
      'Reference Error': [
        'This is likely a programming error',
        'Try refreshing the page',
        'Report this error to the development team',
      ],
      'Syntax Error': [
        'This is likely a programming error in the application',
        'Try refreshing the page',
        'Report this error to the development team',
      ],
    };
    return solutions[errorType] || [];
  };

  generateErrorReport = () => {
    const error = this.state.error;
    const errorInfo = this.state.errorInfo;

    return {
      errorId: this.state.errorId,
      timestamp: new Date().toISOString(),
      errorType: this.getErrorType(error),
      errorSeverity: this.getErrorSeverity(error),
      retryCount: this.state.retryCount,
      error: {
        name: error?.name,
        message: error?.message,
        stack: error?.stack,
        cause: error?.cause,
      },
      errorInfo: {
        componentStack: errorInfo?.componentStack,
      },
      environment: {
        url: window.location.href,
        userAgent: navigator.userAgent,
        screen: `${window.screen.width}x${window.screen.height}`,
        viewport: `${window.innerWidth}x${window.innerHeight}`,
        language: navigator.language,
        platform: navigator.platform,
        cookieEnabled: navigator.cookieEnabled,
        onLine: navigator.onLine,
      },
      performance: {
        memory: performance.memory
          ? {
              usedJSHeapSize: performance.memory.usedJSHeapSize,
              totalJSHeapSize: performance.memory.totalJSHeapSize,
              jsHeapSizeLimit: performance.memory.jsHeapSizeLimit,
            }
          : 'Not available',
        timing: performance.timing
          ? {
              loadEventEnd: performance.timing.loadEventEnd,
              domContentLoadedEventEnd: performance.timing.domContentLoadedEventEnd,
            }
          : 'Not available',
      },
      localStorage: {
        available: typeof Storage !== 'undefined',
        keys: typeof Storage !== 'undefined' ? Object.keys(localStorage) : [],
      },
      sessionStorage: {
        available: typeof Storage !== 'undefined',
        keys: typeof Storage !== 'undefined' ? Object.keys(sessionStorage) : [],
      },
    };
  };

  render() {
    if (this.state.hasError) {
      // If a custom fallback is provided, render it with error details as props
      if (this.props.fallback) {
        return typeof this.props.fallback === 'function'
          ? this.props.fallback({ error: this.state.error, errorInfo: this.state.errorInfo })
          : this.props.fallback;
      }

      // Enhanced error display with comprehensive debugging information
      const error = this.state.error;
      const errorInfo = this.state.errorInfo;
      const errorType = this.getErrorType(error);
      const errorSeverity = this.getErrorSeverity(error);

      const severityColors = {
        low: { bg: '#e8f5e8', border: '#4caf50', text: '#2e7d32' },
        medium: { bg: '#fff3e0', border: '#ff9800', text: '#f57c00' },
        high: { bg: '#ffebee', border: '#f44336', text: '#c62828' },
      };

      const severityColor = severityColors[errorSeverity] || severityColors.medium;

      return (
        <div
          style={{
            padding: 24,
            color: severityColor.text,
            background: severityColor.bg,
            border: `2px solid ${severityColor.border}`,
            borderRadius: 12,
            fontFamily: 'system-ui, -apple-system, sans-serif',
            maxWidth: '100%',
            overflow: 'auto',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
            <h2 style={{ margin: 0, color: severityColor.text, flex: 1 }}>🚨 Application Error Detected</h2>
            <div
              style={{
                background: severityColor.border,
                color: 'white',
                padding: '4px 12px',
                borderRadius: 20,
                fontSize: '12px',
                fontWeight: 'bold',
              }}
            >
              {errorSeverity.toUpperCase()}
            </div>
          </div>

          {/* Error Summary Card */}
          <div
            style={{
              background: 'white',
              padding: 16,
              borderRadius: 8,
              marginBottom: 20,
              border: '1px solid #e0e0e0',
              boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
            }}
          >
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>
              <div>
                <strong>Error Type:</strong> {errorType}
              </div>
              <div>
                <strong>Error ID:</strong>{' '}
                <code style={{ background: '#f5f5f5', padding: '2px 6px', borderRadius: 4 }}>{this.state.errorId}</code>
              </div>
              <div>
                <strong>Retry Count:</strong> {this.state.retryCount}
              </div>
              <div>
                <strong>Timestamp:</strong> {new Date().toLocaleString()}
              </div>
            </div>
          </div>

          {/* Error Message */}
          <div
            style={{
              marginBottom: 20,
              padding: 16,
              background: '#ffebee',
              borderRadius: 4,
              border: '1px solid #ffcdd2',
            }}
          >
            <h3 style={{ margin: '0 0 8px 0', color: '#c62828' }}>Error Message:</h3>
            <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#d32f2f' }}>
              {error?.message || 'Unknown error occurred'}
            </div>
          </div>

          {/* Error Stack Trace */}
          {error?.stack && (
            <details style={{ marginBottom: 20 }}>
              <summary
                style={{ cursor: 'pointer', fontWeight: 'bold', padding: 8, background: '#f5f5f5', borderRadius: 4 }}
              >
                📋 Stack Trace
              </summary>
              <pre
                style={{
                  marginTop: 8,
                  color: '#333',
                  background: '#f8f8f8',
                  padding: 12,
                  borderRadius: 4,
                  overflow: 'auto',
                  fontSize: '12px',
                  lineHeight: '1.4',
                }}
              >
                {error.stack}
              </pre>
            </details>
          )}

          {/* Component Stack */}
          {errorInfo?.componentStack && (
            <details style={{ marginBottom: 20 }}>
              <summary
                style={{ cursor: 'pointer', fontWeight: 'bold', padding: 8, background: '#f5f5f5', borderRadius: 4 }}
              >
                🧩 Component Stack
              </summary>
              <pre
                style={{
                  marginTop: 8,
                  color: '#333',
                  background: '#f8f8f8',
                  padding: 12,
                  borderRadius: 4,
                  overflow: 'auto',
                  fontSize: '12px',
                  lineHeight: '1.4',
                }}
              >
                {errorInfo.componentStack}
              </pre>
            </details>
          )}

          {/* Error Object Details */}
          <details style={{ marginBottom: 20 }}>
            <summary
              style={{ cursor: 'pointer', fontWeight: 'bold', padding: 8, background: '#f5f5f5', borderRadius: 4 }}
            >
              🔍 Full Error Object
            </summary>
            <pre
              style={{
                marginTop: 8,
                color: '#333',
                background: '#f8f8f8',
                padding: 12,
                borderRadius: 4,
                overflow: 'auto',
                fontSize: '12px',
                lineHeight: '1.4',
              }}
            >
              {JSON.stringify(
                {
                  name: error?.name,
                  message: error?.message,
                  stack: error?.stack,
                  cause: error?.cause,
                  ...(error &&
                    Object.getOwnPropertyNames(error).reduce((acc, key) => {
                      if (!['name', 'message', 'stack', 'cause'].includes(key)) {
                        acc[key] = error[key];
                      }
                      return acc;
                    }, {})),
                },
                null,
                2,
              )}
            </pre>
          </details>

          {/* Error Info Details */}
          {errorInfo && (
            <details style={{ marginBottom: 20 }}>
              <summary
                style={{ cursor: 'pointer', fontWeight: 'bold', padding: 8, background: '#f5f5f5', borderRadius: 4 }}
              >
                📊 Error Info Details
              </summary>
              <pre
                style={{
                  marginTop: 8,
                  color: '#333',
                  background: '#f8f8f8',
                  padding: 12,
                  borderRadius: 4,
                  overflow: 'auto',
                  fontSize: '12px',
                  lineHeight: '1.4',
                }}
              >
                {JSON.stringify(errorInfo, null, 2)}
              </pre>
            </details>
          )}

          {/* Browser Information */}
          <details style={{ marginBottom: 20 }}>
            <summary
              style={{ cursor: 'pointer', fontWeight: 'bold', padding: 8, background: '#f5f5f5', borderRadius: 4 }}
            >
              🌐 Environment Information
            </summary>
            <div
              style={{
                marginTop: 8,
                color: '#333',
                background: '#f8f8f8',
                padding: 12,
                borderRadius: 4,
                fontSize: '12px',
                lineHeight: '1.4',
              }}
            >
              <div>
                <strong>User Agent:</strong> {navigator.userAgent}
              </div>
              <div>
                <strong>URL:</strong> {window.location.href}
              </div>
              <div>
                <strong>Timestamp:</strong> {new Date().toISOString()}
              </div>
              <div>
                <strong>Screen:</strong> {window.screen.width}x{window.screen.height}
              </div>
              <div>
                <strong>Viewport:</strong> {window.innerWidth}x{window.innerHeight}
              </div>
            </div>
          </details>

          {/* Suggested Solutions */}
          {this.getSuggestedSolutions(errorType).length > 0 && (
            <details style={{ marginBottom: 20 }}>
              <summary
                style={{
                  cursor: 'pointer',
                  fontWeight: 'bold',
                  padding: 12,
                  background: '#e3f2fd',
                  borderRadius: 8,
                  border: '1px solid #bbdefb',
                }}
              >
                💡 Suggested Solutions
              </summary>
              <div style={{ marginTop: 12, padding: 16, background: '#f8f9fa', borderRadius: 8 }}>
                <ul style={{ margin: 0, paddingLeft: 20 }}>
                  {this.getSuggestedSolutions(errorType).map((solution, index) => (
                    <li key={index} style={{ marginBottom: 8, lineHeight: 1.5 }}>
                      {solution}
                    </li>
                  ))}
                </ul>
              </div>
            </details>
          )}

          {/* Action Buttons */}
          <div style={{ marginTop: 20, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={this.handleRetry}
              style={{
                padding: '10px 20px',
                background: '#2196f3',
                color: 'white',
                border: 'none',
                borderRadius: 6,
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 'bold',
                boxShadow: '0 2px 4px rgba(33, 150, 243, 0.3)',
              }}
            >
              🔄 Try Again
            </button>
            <button
              type="button"
              onClick={() => window.location.reload()}
              style={{
                padding: '10px 20px',
                background: '#ff9800',
                color: 'white',
                border: 'none',
                borderRadius: 6,
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 'bold',
                boxShadow: '0 2px 4px rgba(255, 152, 0, 0.3)',
              }}
            >
              🔄 Reload Page
            </button>
            <button
              type="button"
              onClick={() => {
                const errorReport = this.generateErrorReport();
                console.log('Full Error Report:', errorReport);
                navigator.clipboard?.writeText(JSON.stringify(errorReport, null, 2));
                alert('Error details copied to clipboard and console!');
              }}
              style={{
                padding: '10px 20px',
                background: '#4caf50',
                color: 'white',
                border: 'none',
                borderRadius: 6,
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 'bold',
                boxShadow: '0 2px 4px rgba(76, 175, 80, 0.3)',
              }}
            >
              📋 Copy Error Details
            </button>
            <button
              type="button"
              onClick={() => {
                const errorReport = this.generateErrorReport();
                const mailtoLink = `mailto:support@example.com?subject=Error Report ${this.state.errorId}&body=${encodeURIComponent(JSON.stringify(errorReport, null, 2))}`;
                window.open(mailtoLink);
              }}
              style={{
                padding: '10px 20px',
                background: '#9c27b0',
                color: 'white',
                border: 'none',
                borderRadius: 6,
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 'bold',
                boxShadow: '0 2px 4px rgba(156, 39, 176, 0.3)',
              }}
            >
              📧 Report Issue
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
